import type { Annotation, Cta, DemoDoc, Hotspot, Step } from './demo';

/**
 * Personalisation placeholders: `{key}` or `{key|fallback}` inside any
 * viewer-facing string ("Welcome, {company}!", "{company|your team}").
 *
 * Shared by the player (fills values from the share URL or the embed's
 * `data-vars`, `apps/player/src/personalize.ts`), the MP4 exporter and Studio
 * (both resolve with no values, so a video shows each fallback, never the raw
 * braces). Kept zod-free, with type-only imports, so the player can import it
 * through the `@mado/schema/personalize` subpath without pulling zod into its
 * 60 KB budget.
 */

/** `{company}` or `{company|your team}`. Keys are `[a-z_]`, matching the
 *  sanitiser the player and apps/web's query-param filter apply. */
const PLACEHOLDER = /\{([a-z_]{1,32})(?:\|([^{}]{0,60}))?\}/g;

/**
 * Replace every placeholder in `text`. An unknown key with a fallback uses
 * it; an unknown key with none renders as empty — never the raw `{key}` —
 * so a viewer who arrived without `?company=` sees clean copy, not template
 * syntax.
 */
export function substitute(text: string, vars: Record<string, string>): string {
  if (!text.includes('{')) return text;
  return text.replace(
    PLACEHOLDER,
    (_m, key: string, fallback?: string) => vars[key] ?? fallback ?? '',
  );
}

function personalizeAnnotation(a: Annotation, vars: Record<string, string>): Annotation {
  return { ...a, text: substitute(a.text, vars) };
}

function personalizeHotspot(h: Hotspot, vars: Record<string, string>): Hotspot {
  return h.label ? { ...h, label: substitute(h.label, vars) } : h;
}

function personalizeStep(s: Step, vars: Record<string, string>): Step {
  return {
    ...s,
    name: substitute(s.name, vars),
    alt: substitute(s.alt, vars),
    annotation: s.annotation ? personalizeAnnotation(s.annotation, vars) : s.annotation,
    hotspots: s.hotspots.map((h) => personalizeHotspot(h, vars)),
  };
}

function personalizeCta(c: Cta, vars: Record<string, string>): Cta {
  return {
    ...c,
    label: substitute(c.label, vars),
    headline: c.headline ? substitute(c.headline, vars) : c.headline,
  };
}

/**
 * Whether any viewer-facing text in `doc` uses placeholder syntax at all.
 * `vars` being empty is NOT the same question — a `{company|your team}`
 * still has a fallback to resolve even when nobody supplied `?company=`, so
 * the one case that may skip substitution entirely is a document with no
 * `{` anywhere in its text, never "no vars were given".
 */
function hasPlaceholders(doc: DemoDoc): boolean {
  if (doc.title.includes('{')) return true;
  for (const c of doc.chapters) if (c.title.includes('{')) return true;
  if (doc.cta && (doc.cta.label.includes('{') || !!doc.cta.headline?.includes('{'))) return true;
  for (const s of doc.steps) {
    if (s.name.includes('{') || s.alt.includes('{')) return true;
    if (s.annotation?.text.includes('{')) return true;
    if (s.hotspots.some((h) => h.label?.includes('{'))) return true;
  }
  return false;
}

/**
 * Substitute placeholders through the whole document. Returns `doc` itself
 * (no clone) when it has no placeholder syntax at all — the common case for
 * a document nobody wrote `{...}` into, regardless of whether `vars` is
 * empty.
 */
export function personalizeDoc(doc: DemoDoc, vars: Record<string, string>): DemoDoc {
  if (!hasPlaceholders(doc)) return doc;
  return {
    ...doc,
    title: substitute(doc.title, vars),
    chapters: doc.chapters.map((c) => ({ ...c, title: substitute(c.title, vars) })),
    cta: doc.cta ? personalizeCta(doc.cta, vars) : doc.cta,
    steps: doc.steps.map((s) => personalizeStep(s, vars)),
  };
}
